import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth, isTermsGate, setAuthToken, setUnauthorizedHandler, type AmrClaim, type LoginNextStep } from '@/api/client';
import { setDisplayTimeZone } from '@/lib/dates';

/** Decode the `exp` claim from a JWT without verifying the signature. */
function jwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Milliseconds until 2 minutes before the token expires. Negative = already too late. */
function msUntilRefresh(token: string): number {
  const exp = jwtExp(token);
  if (exp === null) return -1;
  return exp * 1000 - Date.now() - 2 * 60 * 1000;
}

/**
 * Auth context — owns:
 *   - Session token + user (stored in sessionStorage)
 *   - In-flight challenge (mid-login state during MFA)
 *   - Login / logout / site-switch actions
 *
 * Idle-logout (15 min) is wired in via `useIdleTimeout` consumed by AppShell.
 */

/**
 * Keep this in sync with the backend `Role` union in
 * `DreamIT-Backend/src/types/domain.ts`. The `string` fallback is intentional
 * — handlers send the role straight through, and we don't want a frontend
 * deploy lag to break login for a freshly-added role.
 */
export type UserRole =
  // Platform (4)
  | 'platform_admin'
  | 'application_admin'
  | 'compliance'
  | 'study_designer'
  // Site (10)
  | 'site_admin'
  | 'principal_investigator'
  | 'sub_investigator'
  | 'coordinator'
  | 'site_study_designer'
  | 'regulatory_specialist'
  | 'site_pharma'
  | 'finance_billing'
  | 'monitor'
  | 'auditor'
  | 'clinical_nurse'
  | 'clinical_lab_assistant'
  | 'clinical_qe'
  | 'external_lab'
  /** F5 - base site membership before study delegation. See BE Role union. */
  | 'site_user'
  // Sponsor / regulatory (5)
  | 'irb_administrator'
  | 'sponsor_regulatory_affairs'
  | 'regulatory_inspector'
  | 'data_manager'
  | 'sponsor_project_manager'
  // Forward-compat
  | string;

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  activeSiteId: string;
  allowedSites: string[];
  siteNames: Record<string, string>;
  activeSiteTimeZone?: string | null;
  amr?: AmrClaim[];
}

export interface PendingChallenge {
  token: string;
  step: LoginNextStep;
  /** Set when user has switched to email-OTP path. */
  otpChallengeToken?: string;
  otpDestinationMasked?: string;
  /** Carry the originating email so MFA pages can show context. */
  email: string;
  /** F3 - set on the T&C challenge; echoed back to /auth/accept-terms so the
   *  minted session's `amr` faithfully reflects how the user authenticated. */
  amr?: AmrClaim[];
  /** F3 - set on the T&C challenge so the FE knows which version to POST. */
  termsVersion?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  pendingChallenge: PendingChallenge | null;
  /** Step 1 of login. Returns the next step; the MFA page reads `pendingChallenge`. */
  loginPassword: (email: string, password: string) => Promise<LoginNextStep>;
  /** Step 2 — TOTP / recovery code / email-OTP code.
   * Returns `'terms_acceptance_required'` when the server has diverted
   * the flow to the T&C page; the caller should navigate to
   * `/auth/accept-terms`. Returns `'signed_in'` on happy path. */
  loginMfa: (
    method: 'totp' | 'email' | 'recovery',
    code: string,
  ) => Promise<'signed_in' | 'terms_acceptance_required'>;
  /** F3 - consume the terms-acceptance challenge and mint the session. */
  acceptTerms: () => Promise<void>;
  /** Switch to the email-OTP code path; mutates pendingChallenge. */
  requestOtpFallback: () => Promise<void>;
  /** Abandon the in-flight login. */
  cancelChallenge: () => void;
  /** Manually overwrite the challenge (used by MFA enrol-verify flow). */
  setChallenge: (next: PendingChallenge | null) => void;
  /** Sign out. `reason='idle'` triggers the friendly inactivity message. */
  logout: (reason?: 'manual' | 'idle') => Promise<void>;
  /** Move to a different site within the session. Pass siteName when switching to a brand-new site not yet in the session's siteNames map. */
  switchSite: (siteId: string, siteName?: string) => Promise<void>;
  /**
   * On-demand version of the silent background token refresh — re-pulls
   * allowed_sites/site_names/active site from the server and applies them to
   * the live session immediately, instead of waiting for the ~13-minute
   * timer. Call this after any action that can change the caller's own site
   * list (e.g. disabling/enabling a site) so the SiteSwitcher reflects it
   * right away. No-op if the call fails (network blip) — the periodic timer
   * will still catch up.
   */
  refreshSession: () => Promise<void>;
  /** Tracks the last logout reason so the LoginPage can show "you were signed out for inactivity". */
  lastLogoutReason: 'manual' | 'idle' | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SESSION_KEY = 'dreamit2:session';
const CHALLENGE_KEY = 'dreamit2:challenge';

interface PersistedSession {
  token: string;
  user: AuthUser;
}

function loadSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.user.siteNames) parsed.user.siteNames = {};
    return parsed;
  } catch {
    return null;
  }
}

function loadChallenge(): PendingChallenge | null {
  try {
    const raw = sessionStorage.getItem(CHALLENGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingChallenge;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(() => {
    const session = loadSession();
    if (session) setAuthToken(session.token); // sync — so queries don't fire without a token
    return session?.user ?? null;
  });
  const [pendingChallenge, setPendingChallengeState] = useState<PendingChallenge | null>(() =>
    loadChallenge(),
  );
  const [lastLogoutReason, setLastLogoutReason] = useState<'manual' | 'idle' | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule proactive token refresh on first mount (token already set synchronously above).
  useEffect(() => {
    const session = loadSession();
    if (session) scheduleRefresh(session.token);
    return () => clearRefreshTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wire the API client's 401 handler so an expired/revoked JWT triggers
  // a clean session wipe + redirect to /login. Without this, the dashboard
  // renders from cached `user` but every fetch returns 401.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(CHALLENGE_KEY);
      setAuthToken(null);
      setUser(null);
      setPendingChallengeState(null);
      setLastLogoutReason('idle');
      // Hard redirect — avoids needing useNavigate inside the provider.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const setChallenge = useCallback((next: PendingChallenge | null) => {
    if (next) {
      sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(CHALLENGE_KEY);
    }
    setPendingChallengeState(next);
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Forward-declared via ref so scheduleRefresh can call itself recursively
  // without creating a dependency cycle with persistSession.
  const scheduleRefreshRef = useRef<(token: string) => void>(() => undefined);

  const scheduleRefresh = useCallback((token: string) => {
    clearRefreshTimer();
    const delay = msUntilRefresh(token);
    if (delay < 0) return; // token already past the refresh window — logout handles it
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const r = await auth.refresh();
        const existing = loadSession();
        if (!existing) return;
        // Apply the full response, not just the token — allowed_sites/site_names/
        // active site can all change server-side (e.g. a site got disabled) and
        // must reach the live user object, not just the Authorization header.
        const updated: PersistedSession = {
          ...existing,
          token: r.access_token,
          user: {
            ...existing.user,
            activeSiteId: r.site_id,
            allowedSites: r.allowed_sites,
            siteNames: r.site_names,
            activeSiteTimeZone: r.site_time_zone,
          },
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
        setAuthToken(r.access_token);
        setUser(updated.user);
        setDisplayTimeZone(updated.user.activeSiteTimeZone);
        scheduleRefreshRef.current(r.access_token);
      } catch {
        // Refresh failed (token expired before we could renew) — force re-login.
        clearRefreshTimer();
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(CHALLENGE_KEY);
        setAuthToken(null);
        setUser(null);
        setPendingChallengeState(null);
        setLastLogoutReason('idle');
      }
    }, delay);
  }, [clearRefreshTimer]);

  // Keep the ref in sync so the recursive call inside setTimeout is always current.
  scheduleRefreshRef.current = scheduleRefresh;

  const persistSession = useCallback((session: PersistedSession) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    setAuthToken(session.token);
    setUser(session.user);
    setDisplayTimeZone(session.user.activeSiteTimeZone);
    scheduleRefresh(session.token);
  }, [scheduleRefresh]);

  useEffect(() => {
    setDisplayTimeZone(user?.activeSiteTimeZone ?? null);
  }, [user?.activeSiteTimeZone]);

  const clearSession = useCallback(() => {
    clearRefreshTimer();
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(CHALLENGE_KEY);
    setAuthToken(null);
    setUser(null);
    setPendingChallengeState(null);
  }, [clearRefreshTimer]);

  const loginPassword = useCallback<AuthContextValue['loginPassword']>(
    async (email, password) => {
      const result = await auth.login(email, password);
      setChallenge({ token: result.challenge_token, step: result.next, email });
      return result.next;
    },
    [setChallenge],
  );

  const requestOtpFallback = useCallback(async () => {
    if (!pendingChallenge) throw new Error('No active challenge');
    const r = await auth.mfaOtpRequest(pendingChallenge.token);
    setChallenge({
      ...pendingChallenge,
      token: r.challenge_token,
      step: r.next,
      otpChallengeToken: r.otp_challenge_token,
      otpDestinationMasked: r.destination_masked,
    });
  }, [pendingChallenge, setChallenge]);

  const loginMfa = useCallback<AuthContextValue['loginMfa']>(
    async (method, code) => {
      if (!pendingChallenge) throw new Error('No active challenge');
      const r = await auth.loginMfa({
        challenge_token: pendingChallenge.token,
        method,
        code,
        otp_challenge_token: pendingChallenge.otpChallengeToken,
      });

      // F3 - server diverted to the T&C acceptance page. Replace the
      // in-flight challenge with the terms-acceptance one; the AcceptTermsPage
      // reads pendingChallenge to complete the flow.
      if (isTermsGate(r)) {
        setChallenge({
          ...pendingChallenge,
          token: r.challenge_token,
          step: 'terms_acceptance_required',
          amr: r.amr,
          termsVersion: r.terms_version,
        });
        return 'terms_acceptance_required' as const;
      }

      persistSession({
        token: r.access_token,
        user: {
          id: r.user.user_id,
          email: r.user.email,
          fullName: r.user.full_name,
          role: r.user.role,
          activeSiteId: r.user.site_id,
          allowedSites: r.user.allowed_sites,
          siteNames: r.user.site_names ?? {},
          activeSiteTimeZone: r.user.site_time_zone ?? null,
        },
      });
      setChallenge(null);
      setLastLogoutReason(null);
      return 'signed_in' as const;
    },
    [pendingChallenge, persistSession, setChallenge],
  );

  // F3 - AcceptTermsPage calls this after the user reads + clicks Accept.
  const acceptTerms = useCallback(async () => {
    if (!pendingChallenge) throw new Error('No active challenge');
    if (pendingChallenge.step !== 'terms_acceptance_required') {
      throw new Error('Current challenge is not the T&C acceptance step');
    }
    if (!pendingChallenge.termsVersion) {
      throw new Error('Terms version missing from challenge');
    }
    const r = await auth.acceptTerms({
      challenge_token: pendingChallenge.token,
      accepted_version: pendingChallenge.termsVersion,
      amr: pendingChallenge.amr ?? ['pwd'],
    });
    persistSession({
      token: r.access_token,
      user: {
        id: r.user.user_id,
        email: r.user.email,
        fullName: r.user.full_name,
        role: r.user.role,
        activeSiteId: r.user.site_id,
        allowedSites: r.user.allowed_sites,
        siteNames: r.user.site_names ?? {},
        activeSiteTimeZone: r.user.site_time_zone ?? null,
      },
    });
    setChallenge(null);
    setLastLogoutReason(null);
  }, [pendingChallenge, persistSession, setChallenge]);

  const cancelChallenge = useCallback(() => setChallenge(null), [setChallenge]);

  const logout = useCallback(
    async (reason: 'manual' | 'idle' = 'manual') => {
      try {
        if (user) await auth.logout(reason === 'idle');
      } catch {
        // Best-effort — clear local state even if server call fails.
      }
      clearSession();
      setLastLogoutReason(reason);
    },
    [user, clearSession],
  );

  const switchSite = useCallback(
    async (siteId: string, siteName?: string) => {
      if (!user) throw new Error('Not signed in');
      const r = await auth.switchSite(siteId);
      const existing = loadSession();
      if (!existing) return;
      const updatedUser = { ...existing.user, activeSiteId: r.site_id, role: r.role, activeSiteTimeZone: r.site_time_zone };
      if (!updatedUser.allowedSites.includes(r.site_id)) {
        updatedUser.allowedSites = [...updatedUser.allowedSites, r.site_id];
      }
      if (siteName) {
        updatedUser.siteNames = { ...updatedUser.siteNames, [r.site_id]: siteName };
      }
      persistSession({ ...existing, token: r.access_token, user: updatedUser });
      // Every cached query is scoped to whatever site was active when it
      // fetched, but no query key carries the site id itself — so this is
      // the one place that must invalidate all of them. Owned here (not by
      // each caller) after a bug where SiteOnboardPage's auto-switch-after-
      // create forgot to clear the cache, leaving the previous site's
      // Document Library folders rendered under the new site until
      // something unrelated happened to refetch them.
      qc.clear();
    },
    [user, persistSession, qc],
  );

  const refreshSession = useCallback(async () => {
    if (!user) return;
    try {
      const r = await auth.refresh();
      const existing = loadSession();
      if (!existing) return;
      persistSession({
        ...existing,
        token: r.access_token,
        user: {
          ...existing.user,
          activeSiteId: r.site_id,
          allowedSites: r.allowed_sites,
          siteNames: r.site_names,
          activeSiteTimeZone: r.site_time_zone,
        },
      });
    } catch {
      // Best-effort — the periodic background refresh will catch up if this blips.
    }
  }, [user, persistSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      pendingChallenge,
      loginPassword,
      loginMfa,
      acceptTerms,
      requestOtpFallback,
      cancelChallenge,
      setChallenge,
      logout,
      switchSite,
      refreshSession,
      lastLogoutReason,
    }),
    [user, pendingChallenge, loginPassword, loginMfa, acceptTerms, requestOtpFallback, cancelChallenge, setChallenge, logout, switchSite, refreshSession, lastLogoutReason],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
